/**
 * Flow-limit refusal — the one place the client explains "you can't create
 * another knod".
 *
 * ## Why this exists
 *
 * Flows are browser-authoritative: the React client writes rows to `flows`
 * itself via the Supabase JS client, so the per-tier `max_flows` cap is
 * enforced by a BEFORE INSERT trigger in the database
 * (supabase/migrations/099_flows_count_limit.sql):
 *
 *   RAISE EXCEPTION 'FLOW_LIMIT_REACHED: plan allows % flows', v_limit;
 *
 * The headless API already translates that into a 402 carrying `maxFlows`
 * (server/errors/index.ts). The browser never goes through that route, so it
 * receives the raw Postgres error instead — and every call site used to
 * swallow it into a `console.error` or a generic "trouble saving your work".
 * The number the trigger went to the trouble of reporting never reached a
 * human.
 *
 * `parseFlowLimitError` mirrors the server's `translatePostgresError` so both
 * shapes — the raw trigger message and the API's `{ code, details }` envelope —
 * resolve to the same thing, and `reportFlowLimitRefusal` is the single handler
 * every creation path calls. Copy lives here too, so the refusal dialog and the
 * dashboard counter can never disagree about what the limit is or what to do
 * about it.
 *
 * ## Deleting frees a slot; archiving does not
 *
 * The trigger counts every row the user owns:
 *
 *   SELECT COUNT(*) FROM public.flows WHERE user_id = NEW.user_id
 *
 * There is no `archived = false` filter, so archiving a knod does NOT free a
 * slot. The copy below says "delete" and never "archive" on purpose — telling
 * someone to archive would send them round a loop that cannot succeed.
 */

import { alertService, type AlertAction } from '../components/Alert';

/**
 * The literal prefix raised by the migration-099 trigger. Shared with
 * `server/errors/index.ts`, which pins the same string.
 */
export const FLOW_LIMIT_CODE = 'FLOW_LIMIT_REACHED';

/** `max_flows = -1` is the "unlimited" sentinel (migration 097). */
export const UNLIMITED_FLOWS = -1;

/** A refusal, with the plan's cap when the error carried one. */
export interface FlowLimitInfo {
  /** How many flows the plan allows, or null when the error didn't say. */
  maxFlows: number | null;
}

/**
 * Typed refusal thrown by the storage layer so call sites don't have to
 * re-parse a Postgres message string. Carries the cap for the UI.
 */
export class FlowLimitReachedError extends Error {
  readonly code = FLOW_LIMIT_CODE;
  readonly maxFlows: number | null;

  constructor(maxFlows: number | null, message?: string) {
    super(message ?? buildFlowLimitMessage({ maxFlows }));
    this.name = 'FlowLimitReachedError';
    this.maxFlows = maxFlows;
  }
}

function readMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message ?? '');
  }
  return '';
}

/**
 * Recognise a flow-limit refusal in any shape we can receive it:
 *
 *  - a `FlowLimitReachedError` we already threw;
 *  - the raw Postgres error from the trigger (possibly wrapped by a caller
 *    that prefixed it, e.g. `Failed to save flow: FLOW_LIMIT_REACHED: …`);
 *  - the headless API's `{ code: 'FLOW_LIMIT_REACHED', details: { maxFlows } }`
 *    402 envelope.
 *
 * Returns null when the error is something else entirely.
 */
export function parseFlowLimitError(error: unknown): FlowLimitInfo | null {
  if (error instanceof FlowLimitReachedError) {
    return { maxFlows: error.maxFlows };
  }

  const record = (error && typeof error === 'object') ? (error as Record<string, unknown>) : null;

  // API envelope: { error, code, details: { maxFlows } }
  if (record && record.code === FLOW_LIMIT_CODE) {
    const details = record.details as Record<string, unknown> | undefined;
    const fromDetails = Number(details?.maxFlows);
    return { maxFlows: Number.isFinite(fromDetails) ? fromDetails : null };
  }

  const message = readMessage(error);
  if (!message.includes(FLOW_LIMIT_CODE)) return null;

  // Same regex as server/errors/index.ts — one contract, two readers.
  const match = message.match(/plan allows (\d+) flows/);
  return { maxFlows: match ? Number(match[1]) : null };
}

/** True when this error is the flow cap refusing an insert. */
export function isFlowLimitError(error: unknown): boolean {
  return parseFlowLimitError(error) !== null;
}

/** True when the plan has no cap. */
export function isUnlimitedFlows(maxFlows: number | null | undefined): boolean {
  return maxFlows === UNLIMITED_FLOWS;
}

const KNOD = (n: number) => `${n} ${n === 1 ? 'knod' : 'knods'}`;

/**
 * The refusal sentence.
 *
 * `used` is optional. When the caller knows it AND it exceeds the cap, the
 * message explains the over-limit case explicitly — someone who subscribed,
 * built twelve knods and then cancelled keeps all twelve (the trigger only
 * fires on INSERT, so nothing is ever deleted). Telling that person "you've
 * reached your limit of 3" while they can see twelve on screen reads as a bug.
 */
export function buildFlowLimitMessage({
  maxFlows,
  used,
}: { maxFlows: number | null; used?: number | null }): string {
  if (maxFlows === null || maxFlows === undefined) {
    return "You've reached the number of knods your plan includes, so this one couldn't be created.";
  }

  if (typeof used === 'number' && used > maxFlows) {
    return (
      `Your plan includes ${KNOD(maxFlows)} and you have ${used}. ` +
      `The knods you already have are safe, but you'll need to be back down to ${maxFlows} ` +
      `— or on a bigger plan — before you can create another.`
    );
  }

  return `You've reached your plan's limit of ${KNOD(maxFlows)}, so this one couldn't be created.`;
}

/**
 * What the user can actually do about it. Deliberately never mentions
 * archiving — see the header note.
 */
export function buildFlowLimitSuggestions(maxFlows: number | null): string[] {
  return [
    'Delete a knod you no longer need — archiving keeps it in the count, only deleting frees a slot.',
    maxFlows === null
      ? 'Upgrade your plan to create more knods.'
      : `Upgrade your plan to go beyond ${KNOD(maxFlows)}.`,
  ];
}

export interface FlowLimitContext {
  /** How many flows the user currently has, when the caller knows. */
  used?: number | null;
  /** Send the user to the pricing page. Omit to hide the Upgrade action. */
  onUpgrade?: () => void;
  /** Send the user to their knods list. Omit to hide the Manage action. */
  onManageKnods?: () => void;
}

/**
 * Show the refusal. Call this from a `catch` around anything that can create a
 * flow, and let it decide whether the error was ours:
 *
 *   catch (error) {
 *     if (reportFlowLimitRefusal(error, { onUpgrade })) return;
 *     // ...normal failure handling
 *   }
 *
 * Returns true when it recognised and displayed the refusal, so a caller can
 * skip its generic fallback — including any retry, which for this error would
 * fail identically.
 */
export function reportFlowLimitRefusal(error: unknown, context: FlowLimitContext = {}): boolean {
  const info = parseFlowLimitError(error);
  if (!info) return false;

  const actions: AlertAction[] = [];
  if (context.onUpgrade) {
    actions.push({ label: 'Upgrade plan', onClick: context.onUpgrade, variant: 'primary' });
  }
  if (context.onManageKnods) {
    actions.push({ label: 'Manage my knods', onClick: context.onManageKnods, variant: 'secondary' });
  }

  alertService.error(
    buildFlowLimitMessage({ maxFlows: info.maxFlows, used: context.used }),
    false, // do not auto-close: this needs a decision, not a glance
    buildFlowLimitSuggestions(info.maxFlows),
    undefined,
    actions.length > 0 ? actions : undefined,
  );

  return true;
}
